import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import FrontCard from "./FrontCard/FrontCard";
import BackCard from "./BackCard/BackCard";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { FlipCardProps, FlipCardFace } from "../../flip-cards.props";
import { getAnalyticsDataAttrs } from "@directv-digital/dds.services.analytics-service";
import { useLocationHref } from "src/hooks/useLocationHref";
import { useRsnFeeValue } from "@directv-digital/dds.server.application-context";
import { getCookie, isValidRsnFee } from "@directv-digital/dds.services.cookie-service";
import { logError } from "lib/helper/logger";

// Must match the 0.6s transform transition below so the inactive face is only
// re-hidden (visibility:hidden) after the 3D rotation has finished playing.
const FLIP_ANIMATION_MS = 600;

// Representative RSN (Regional Sports Fee) used when no zip-specific fee is resolved, so the
// "Up to $X w/ req'd fees" legal copy still reflects RSN. Mirrors plan-card's 19.99 fallback.
const DEFAULT_RSN_FEE = 19.99;

type LegalRsnMeta = {
  useRsnFee: boolean;
  legalOfferPrice: string | null;
  legalRegularPrice: string | null;
};

const applyRsnToFace = (
  face: FlipCardFace | undefined,
  meta: LegalRsnMeta | undefined,
  legalRsnFee: number,
  cardId: string | undefined
): FlipCardFace | undefined => {
  if (!face?.legalText?.content || !meta?.useRsnFee) {
    return face;
  }

  let { content } = face.legalText;

  // Resolve all RSN tokens even when legalRsnFee is 0. Two cases both render correctly at 0:
  //  - genuine 0-RSN zip → adds $0 / shows "$0.00" (mirrors plan-card honoring a valid 0 fee)
  //  - pre-hydration initial 0 → shows the base price, then the post-hydration RSN update is a
  //    normal re-render (SSR and the first client paint both use 0, so no hydration mismatch).
  // [total_price]/[rsn_fee] additionally require legalOfferPrice — when that is null the token
  // stays unresolved and is caught by the safety strip below.
  if (content.includes('[rsn_fee]')) {
    content = content.replace(/\[rsn_fee\]/g, legalRsnFee.toFixed(2));
  }
  if (content.includes('[total_price]') && meta.legalOfferPrice) {
    const total = (parseFloat(meta.legalOfferPrice) + legalRsnFee).toFixed(2);
    content = content.replace(/\[total_price\]/g, total);
  }

  // [first_req_price]/[second_req_price]: base (offer/regular + geminiFee, excl. RSN) is carried
  // in meta; add the zip RSN here (mirrors plan-card's _requiredPriceValuesNoRsn + client RSN).
  if (content.includes('[first_req_price]') && meta.legalOfferPrice) {
    const firstReq = (parseFloat(meta.legalOfferPrice) + legalRsnFee).toFixed(2);
    content = content.replace(/\[first_req_price\]/g, firstReq);
  }
  if (content.includes('[second_req_price]') && meta.legalRegularPrice) {
    const secondReq = (parseFloat(meta.legalRegularPrice) + legalRsnFee).toFixed(2);
    content = content.replace(/\[second_req_price\]/g, secondReq);
  }

  // Safety net: any RSN token still present — fee never resolved, OR [total_price]
  // with a null legalOfferPrice — must never reach regulated legal copy. Strip the
  // token (+ optional preceding $), collapse the spacing artifact, and log once.
  // The server-side guard deliberately excludes these tokens for RSN cards, so this
  // client-side strip is the only thing standing between a raw token and the user.
  if (/\[rsn_fee\]|\[total_price\]|\[first_req_price\]|\[second_req_price\]/.test(content)) {
    logError('FlipCard', new Error('RSN token unresolved — stripping from legal copy'), { cardId });
    content = content
      .replace(/\$?\[rsn_fee\]/g, '')
      .replace(/\$?\[total_price\]/g, '')
      .replace(/\$?\[first_req_price\]/g, '')
      .replace(/\$?\[second_req_price\]/g, '')
      // Collapse the space left before trailing punctuation OR a unit slash (e.g.
      // "Total $[total_price]/mo." → "Total /mo." → "Total/mo."), then squeeze any
      // remaining double-spaces so no spacing artifact reaches regulated legal copy.
      .replace(/\s+([.,;:/])/g, '$1')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  if (content === face.legalText.content) {
    return face;
  }
  return {
    ...face,
    legalText: {
      ...face.legalText,
      content
    }
  };
};

// Interactions on nested controls must not flip the card.
const NESTED_INTERACTIVE_SELECTOR = "a, button, [role=\"checkbox\"]";

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    perspective: "1000px",
    width: {
      xs: "100%",
      sm: "223px",
      md: "373px"
    },
    minWidth: {
      xs: "100%",
      sm: "223px",
      md: "373px"
    },
    borderRadius: "24px"
  },
  inner: {
    position: "relative",
    width: "100%",
    // flex:1 fills the wrapper flex-column, which is a CSS Grid item stretched to
    // the grid row height. This replaces the old height:100% that depended on the
    // EqualHeightElement wrapper providing an explicit parent height.
    flex: 1,
    // Both faces stack in one grid cell → card height = max(front, back) from
    // first paint, no JS measurement. Needs Chrome 117+/Safari 17+ (preserve-3d).
    display: "grid",
    // "1fr" stretches the single row to fill the flex:1 inner height.
    gridTemplateRows: "1fr",
    transition: "transform 0.6s ease-in-out",
    transformStyle: "preserve-3d",
    cursor: "pointer",
    borderRadius: "24px"
  },
  face: {
    gridArea: "1 / 1",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backfaceVisibility: "hidden",
    borderRadius: "24px"
  },
  front: {
    position: "relative"
  },
  back: {
    transform: "rotateY(180deg)"
  }
};

const FlipCard = (props: FlipCardProps) => {
  const { card, isMobile } = props;
  const [isFlipped, setIsFlipped] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const cardId = card?.config?.queryparam;
  const isFlippable = Boolean(card?.backCard);
  const href = useLocationHref();
  const rsnFeeFromContext = useRsnFeeValue();

  // RSN hydration: store only the resolved fee as state so the state change drives a re-render,
  // then compute the hydrated face INLINE during render (not via a memoized intermediate) so
  // React.memo on FrontCard/DynamicText cannot short-circuit the content update.
  //
  // Initial value is always 0 to match SSR output — reading the cookie here would produce a
  // different value than the server rendered, causing a hydration mismatch on regulated legal
  // copy for returning visitors who already have the rsnFee cookie. The useEffect below reads
  // the cookie post-hydration and triggers a single re-render when the fee is available.
  const [legalRsnFee, setLegalRsnFee] = useState(0);
  useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    if (!card?.config?._legalRsnMeta?.useRsnFee) {
      return;
    }
    // Pass RAW values to isValidRsnFee — do NOT coalesce with `|| ""`, which would turn a
    // numeric 0 into "" and lose a genuine 0-RSN zip. The context initializes to undefined
    // (globalRsnFee) until a zip resolves, so undefined = "not resolved" while 0 = "resolved,
    // this region has no RSN".
    const cookieVal = getCookie("rsnFee");
    const contextVal = rsnFeeFromContext;
    // Honor a *valid* resolved fee — including 0 — so a 0-RSN zip adds $0 instead of being
    // treated as unknown. Fall back to the representative default ONLY when neither source
    // resolves a valid fee, so the regulated "Up to $X w/ req'd fees" copy still reflects RSN.
    // Mirrors plan-card, which uses a valid 0 fee as-is and only defaults to 19.99 on error
    // (dtv-plan-card-v2-module handleRsnFeeChange). Cookie takes precedence; context resolves
    // async so the dep on rsnFeeFromContext re-runs this once it becomes available.
    let fee: number;
    if (isValidRsnFee(cookieVal)) {
      fee = parseFloat(String(cookieVal));
    } else if (isValidRsnFee(contextVal)) {
      fee = parseFloat(String(contextVal));
    } else {
      fee = DEFAULT_RSN_FEE;
    }
    setLegalRsnFee(fee);
    // card and getCookie are intentionally omitted: card is a stable prop reference within
    // a render cycle, and getCookie is a stable imported function — neither changes between renders.
  }, [rsnFeeFromContext]);

  // Memoize so isAnimating/isFlipped re-renders don't produce new object references
  // and defeat React.memo on FrontCard/BackCard for non-RSN renders.
  // eslint-disable-next-line no-underscore-dangle
  const rsnMeta = card?.config?._legalRsnMeta;
  const hydratedFrontCard = useMemo(
    () => applyRsnToFace(card?.frontCard, rsnMeta, legalRsnFee, cardId),
    [card?.frontCard, rsnMeta, legalRsnFee, cardId]
  );
  const hydratedBackCard = useMemo(
    () => applyRsnToFace(card?.backCard, rsnMeta, legalRsnFee, cardId),
    [card?.backCard, rsnMeta, legalRsnFee, cardId]
  );

  const isFrontDefault = useMemo(() => card?.config?.defaultCard !== "back", [card]);

  // Cleanup animation timer on unmount
  useEffect(() => () => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }
  }, []);

  const flip = useCallback(() => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }
    setIsAnimating(true);
    setIsFlipped(prev => !prev);
    animationTimerRef.current = setTimeout(() => setIsAnimating(false), FLIP_ANIMATION_MS);
  }, []);

  const handleCardClick = useCallback((e: React.MouseEvent) => {
    if (!isFlippable) {
      return;
    }
    if ((e.target as HTMLElement).closest(NESTED_INTERACTIVE_SELECTOR)) {
      return;
    }
    flip();
  }, [isFlippable, flip]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isFlippable) {
        return;
      }
      if ((e.target as HTMLElement).closest(NESTED_INTERACTIVE_SELECTOR)) {
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        flip();
      }
    },
    [isFlippable, flip]
  );

  const getFlipTransform = () => {
    if (!isFlippable) {
      return "rotateY(0deg)";
    }
    if (isFrontDefault) {
      return isFlipped ? "rotateY(180deg)" : "rotateY(0deg)";
    }
    return isFlipped ? "rotateY(0deg)" : "rotateY(180deg)";
  };

  // Tracks which face is currently visible — used to hide the inactive face from the
  // a11y tree / tab order (visibility:hidden, unlike backface-visibility, removes from both).
  const frontIsVisible = isFrontDefault ? !isFlipped : isFlipped;

  const ariaLabel = useMemo(() => {
    if (!isFlippable) {
      return undefined;
    }
    const name = card?.frontCard?.title?.content || card?.backCard?.title?.content || card?.config?.queryparam || "card";
    // State-suffix follows which face is *actually visible* — not `isFlipped`, which is
    // inverted for cards with defaultCard==="back".
    if (frontIsVisible) {
      return `${name}, showing summary. Press Enter to see details.`;
    }
    return `${name}, showing details. Press Enter to return to summary.`;
  }, [isFlippable, frontIsVisible, card?.frontCard?.title?.content, card?.backCard?.title?.content, card?.config?.queryparam]);

  const analyticsAttrs = useMemo(
    () => (isFlippable ? getAnalyticsDataAttrs(ariaLabel, "panel_product cards", href) : {}),
    [isFlippable, ariaLabel, href]
  );

  try {
    return (
      <DtvBox
        sx={styles.wrapper}
      >
        <DtvBox
          onClick={handleCardClick}
          onKeyDown={handleKeyDown}
          tabIndex={isFlippable ? 0 : undefined}
          role={isFlippable ? "button" : undefined}
          aria-label={ariaLabel}
          {...analyticsAttrs}
          sx={{
            ...styles.inner,
            boxShadow: isAnimating ? "none" : (isFlippable && "0 2px 10px 0 rgba(0, 0, 0, 0.08)"),
            transform: getFlipTransform(),
            ...(!isFlippable && { cursor: "default" })
          }}
        >
          {/* Front Face — hidden from tab order & a11y tree when back is showing */}
          <DtvBox sx={{
            ...styles.face,
            ...styles.front,
            ...(isFlippable && !frontIsVisible && !isAnimating && { visibility: "hidden" })
          }}>
            <FrontCard
              card={hydratedFrontCard}
              isFlippable={isFlippable}
              compareBox={card?.compareBox}
              cardId={cardId}
              isMobile={isMobile}
            />
          </DtvBox>
          {/* Back Face — hidden from tab order & a11y tree when front is showing */}
          {isFlippable && (
            <DtvBox sx={{
              ...styles.face,
              ...styles.back,
              ...(frontIsVisible && !isAnimating && { visibility: "hidden" })
            }}>
              <BackCard
                card={hydratedBackCard}
                isFlippable={isFlippable}
                compareBox={card?.compareBox}
                cardId={cardId}
                isMobile={isMobile}
              />
            </DtvBox>
          )}
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay error={error} component="FlipCard" />;
  }
};

export default React.memo(FlipCard);
